import { initializeSoulmarks } from "./dispatch";
import { reconcileSituation } from "./soulmarkSync";
import { Pet, BattleStatus } from "./pet";
import { Seer } from "./seer";
import { Skill } from "./skill";
import { Move } from "./move";
import { Timing } from "./timing";
import { BattleRandom } from "./random";
import { cloneBattleState } from "./snapshot";
import { opposite, type Operation, type Side } from "./operation";
import type { Context } from "./cond";
import type { BattleEvent, UseSkillResult } from "./events";

export { cloneBattleState } from "./snapshot";
export type { Operation, Side } from "./operation";

/**
 * Situation：封装回合阶段、行动排序与结算快照。
 */
export class Situation {
    /**
     * leftSeer，保存当前实例的leftSeer状态。
     */
    leftSeer: Seer;
    /**
     * rightSeer，保存当前实例的rightSeer状态。
     */
    rightSeer: Seer;
    /**
     * random，保存当前实例的random状态。
     */
    random: BattleRandom;
    /**
     * roundNumber，保存当前实例的roundNumber状态。
     */
    roundNumber = 0;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param leftSeer 左方赛尔
     * @param rightSeer 右方赛尔
     * @param seed 随机数初始种子；默认值为 `Date.now() >>> 0`
     */
    constructor(leftSeer: Seer, rightSeer: Seer, seed = Date.now() >>> 0) {
        this.leftSeer = cloneBattleState(leftSeer);
        this.rightSeer = cloneBattleState(rightSeer);
        this.random = new BattleRandom(seed);
        this.leftSeer.random = this.rightSeer.random = this.random;
    }
}

/**
 * ActionType 的数据结构或可选取值约定。
 */
export type ActionType = "skill" | "switch" | "prop" | "skip" | "settle";

/**
 * RoundStep 的数据结构或可选取值约定。
 */
export interface RoundStep {
    /**
     * side，保存当前实例的side状态。
     */
    side?: Side;
    /**
     * operation，保存当前实例的operation状态。
     */
    operation?: Operation;
    /**
     * actionType，保存当前实例的actionType状态。
     */
    actionType: ActionType;
    /**
     * timing，保存当前实例的timing状态。
     */
    timing?: Timing;
    /**
     * snapshot，保存当前实例的snapshot状态。
     */
    snapshot: Situation;
    /**
     * events，保存当前实例的events状态。
     */
    events: BattleEvent[];
    /**
     * actorName，保存当前实例的actorName状态。
     */
    actorName?: string;
    /**
     * skillName，保存当前实例的skillName状态。
     */
    skillName?: string;
    /**
     * skill，保存当前实例的skill状态。
     */
    skill?: Skill;
    /**
     * propName，保存当前实例的propName状态。
     */
    propName?: string;
    /**
     * result，保存当前实例的result状态。
     */
    result?: UseSkillResult;
    /**
     * criticalHit，保存当前实例的criticalHit状态。
     */
    criticalHit?: boolean;
    /**
     * defeated，保存当前实例的defeated状态。
     */
    defeated?: boolean;
    /**
     * defeatedNames，保存当前实例的defeatedNames状态。
     */
    defeatedNames?: string[];
    /**
     * notExecuted，保存当前实例的notExecuted状态。
     */
    notExecuted?: boolean;
    /**
     * blockReason，保存当前实例的blockReason状态。
     */
    blockReason?: string;
    /**
     * replacement，保存当前实例的replacement状态。
     */
    replacement?: boolean;
}

/** 三个阵亡检查点是领域规则，不由伤害函数或动画提前触发。 */
export class Round {
    /**
     * leftMove，保存当前实例的leftMove状态。
     */
    leftMove: Move;
    /**
     * rightMove，保存当前实例的rightMove状态。
     */
    rightMove: Move;
    /**
     * initialSnapshot，保存当前实例的initialSnapshot状态。
     */
    initialSnapshot!: Situation;
    /**
     * finalSnapshot，保存当前实例的finalSnapshot状态。
     */
    finalSnapshot!: Situation;
    /**
     * steps，保存当前实例的steps状态。
     */
    steps: RoundStep[] = [];
    /**
     * events，保存当前实例的events状态。
     */
    events: BattleEvent[] = [];
    /**
     * actionOrder，保存当前实例的actionOrder状态。
     */
    actionOrder: Side[] = [];
    /**
     * effectivePriority，保存当前实例的effectivePriority状态。
     */
    effectivePriority: Record<Side, number> = { left: 0, right: 0 };

    /**
     * 创建实例并初始化所需的领域状态。
     * @param left left参数，参与本次操作
     * @param right right参数，参与本次操作
     * @param leftReplacement leftReplacement参数，参与本次操作
     * @param rightReplacement rightReplacement参数，参与本次操作
     */
    constructor(
        left: Operation,
        right: Operation,
        public leftReplacement?: number,
        public rightReplacement?: number,
    ) {
        this.leftMove = new Move(cloneBattleState(left));
        this.rightMove = new Move(cloneBattleState(right));
    }

    /**
     * getMove：执行对应的领域操作。
     * @param side 操作所属的一方
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    getMove(side: Side): Move {
        return side === "left" ? this.leftMove : this.rightMove;
    }
    /**
     * seer：执行对应的领域操作。
     * @param work 本回合正在结算的工作局面
     * @param side 操作所属的一方
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private seer(work: Situation, side: Side): Seer {
        return side === "left" ? work.leftSeer : work.rightSeer;
    }

    /**
     * context：执行对应的领域操作。
     * @param work 本回合正在结算的工作局面
     * @param side 操作所属的一方
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private context(work: Situation, side: Side): Context | undefined {
        /**
         * sbjSeer，供本模块相关规则使用。
         */
        const sbjSeer = this.seer(work, side);
        /**
         * objSeer，供本模块相关规则使用。
         */
        const objSeer = this.seer(work, opposite(side));
        /**
         * sbjPet，供本模块相关规则使用。
         */
        const sbjPet = sbjSeer.getActivePet();
        /**
         * objPet，供本模块相关规则使用。
         */
        const objPet = objSeer.getBattlePet();
        if (!sbjPet || !objPet) return;
        return { sbjPet, objPet, sbjSeer, objSeer, side, events: this.events };
    }

    /**
     * resetBoards：执行对应的领域操作。
     * @param work 本回合正在结算的工作局面
     */
    private resetBoards(work: Situation) {
        work.leftSeer.getBattlePet()?.resetBattleBoard();
        work.rightSeer.getBattlePet()?.resetBattleBoard();
    }

    /**
     * step：执行对应的领域操作。
     * @param work 本回合正在结算的工作局面
     * @param data data参数，参与本次操作
     * @param from from参数，参与本次操作
     */
    private step(work: Situation, data: Omit<RoundStep, "snapshot" | "events">, from: number) {
        reconcileSituation(work);
        this.steps.push({
            ...data,
            snapshot: cloneBattleState(work),
            events: cloneBattleState(this.events.slice(from)),
        });
    }

    /**
     * compute：执行对应的领域操作。
     * @param situation 用于推演的初始局面
     */
    compute(situation: Situation) {
        /**
         * work，供本模块相关规则使用。
         */
        const work = cloneBattleState(situation);
        initializeSoulmarks(work.leftSeer, work.rightSeer);
        reconcileSituation(work);
        this.initialSnapshot = cloneBattleState(work);
        this.steps = [];
        this.events = [];
        work.roundNumber++;
        for (const seer of [work.leftSeer, work.rightSeer]) {
            seer.先出手 = undefined;
            for (const pet of seer.petPackage) pet.roundNumber = work.roundNumber;
        }
        for (const side of ["left", "right"] as Side[]) {
            /**
             * replacement，供本模块相关规则使用。
             */
            const replacement = side === "left" ? this.leftReplacement : this.rightReplacement;
            if (replacement !== undefined) this.switchPet(work, side, replacement, true);
        }
        // 主动切换不占用技能排序位置，所有切换在回合开始前完成。
        for (const side of ["left", "right"] as Side[]) {
            /**
             * operation，供本模块相关规则使用。
             */
            const operation = this.getMove(side).operation;
            if (operation.kind === "switch") this.switchPet(work, side, operation.petId, false);
        }
        this.globalPhase(work, Timing.roundBegin, ["right", "left"]);
        this.globalPhase(work, Timing.battleStageBegin, ["right", "left"]);
        /**
         * orderEventsFrom，供本模块相关规则使用。
         */
        const orderEventsFrom = this.events.length;
        this.actionOrder = this.order(work);
        this.steps[this.steps.length - 1].events.push(
            ...cloneBattleState(this.events.slice(orderEventsFrom)),
        );
        this.seer(work, this.actionOrder[0]).先出手 = true;
        this.seer(work, this.actionOrder[1]).先出手 = false;
        // 排序结果写入阶段快照，回看时与实际行动一致。
        reconcileSituation(work);
        this.steps[this.steps.length - 1].snapshot = cloneBattleState(work);
        for (const side of this.actionOrder) this.act(work, side);
        this.globalPhase(work, Timing.battleStageEnd, ["left", "right"]);
        this.globalPhase(work, Timing.roundEnd, ["left", "right"], true);
        for (const seer of [work.leftSeer, work.rightSeer]) {
            for (const pet of seer.petPackage) pet.expireRoundProtection();
        }
        this.globalPhase(work, Timing.afterRoundEnd, ["left", "right"]);
        // 三次判定之外造成的0体力不触发第四次阵亡时机。
        for (const seer of [work.leftSeer, work.rightSeer]) {
            for (const pet of seer.petPackage) {
                if (pet.战斗状态 !== BattleStatus.DEAD && pet.当前体力 <= 0) pet.当前体力 = 1;
            }
        }
        reconcileSituation(work);
        this.finalSnapshot = cloneBattleState(work);
        this.steps[this.steps.length - 1].snapshot = cloneBattleState(work);
    }

    /**
     * switchPet：执行对应的领域操作。
     * @param work 本回合正在结算的工作局面
     * @param side 操作所属的一方
     * @param petId 精灵唯一序号
     * @param replacement replacement参数，参与本次操作
     */
    private switchPet(work: Situation, side: Side, petId: number, replacement: boolean) {
        this.resetBoards(work);
        /**
         * from，供本模块相关规则使用。
         */
        const from = this.events.length;
        /**
         * seer，供本模块相关规则使用。
         */
        const seer = this.seer(work, side);
        /**
         * incoming，供本模块相关规则使用。
         */
        const incoming = seer.petPackage.find((pet) => pet.序号 === petId);
        /**
         * opponent，供本模块相关规则使用。
         */
        const opponent = this.seer(work, opposite(side));
        if (!incoming || incoming.战斗状态 !== BattleStatus.STANDBY || !incoming.isAlive()) return;
        if (!seer.changeBattlePet(petId, opponent, { side, events: this.events })) return;
        this.step(
            work,
            {
                side,
                operation: { kind: "switch", petId },
                actionType: "switch",
                timing: Timing.petOn,
                replacement,
                actorName: incoming.名字,
            },
            from,
        );
    }

    /**
     * globalPhase：执行对应的领域操作。
     * @param work 本回合正在结算的工作局面
     * @param timing 需要处理的战斗时机
     * @param sides sides参数，参与本次操作
     * @param detect detect参数，参与本次操作；默认值为 `false`
     */
    private globalPhase(work: Situation, timing: Timing, sides: Side[], detect = false) {
        this.resetBoards(work);
        /**
         * from，供本模块相关规则使用。
         */
        const from = this.events.length;
        for (const side of sides) {
            this.events.push({ timing, side });
            /**
             * context，供本模块相关规则使用。
             */
            const context = this.context(work, side);
            if (!context) continue;
            context.timing = timing;
            if (timing === Timing.roundBegin) context.sbjPet.activateNextRoundReward(context);
            context.sbjPet.runGlobalTiming(timing, context);
        }
        /**
         * defeatedNames，供本模块相关规则使用。
         */
        const defeatedNames = detect ? this.detectDefeat(work) : [];
        this.step(
            work,
            { actionType: "settle", timing, defeatedNames, defeated: defeatedNames.length > 0 },
            from,
        );
    }

    /**
     * act：执行对应的领域操作。
     * @param work 本回合正在结算的工作局面
     * @param side 操作所属的一方
     */
    private act(work: Situation, side: Side) {
        this.resetBoards(work);
        /**
         * from，供本模块相关规则使用。
         */
        const from = this.events.length;
        /**
         * move，供本模块相关规则使用。
         */
        const move = this.getMove(side);
        /**
         * operation，供本模块相关规则使用。
         */
        const operation = move.operation;
        /**
         * context，供本模块相关规则使用。
         */
        const context = this.context(work, side);
        /**
         * actor，供本模块相关规则使用。
         */
        const actor = context?.sbjPet;
        /**
         * skill，供本模块相关规则使用。
         */
        const skill =
            operation.kind === "skill" && actor?.序号 === operation.petId
                ? actor.技能组[operation.skillIndex]
                : undefined;
        /**
         * result，供本模块相关规则使用。
         */
        let result: UseSkillResult | undefined;
        /**
         * executed，供本模块相关规则使用。
         */
        let executed = !!actor;
        /**
         * blockReason，供本模块相关规则使用。
         */
        let blockReason: string | undefined;
        this.events.push({ timing: Timing.actionBegin, side });
        if (context) {
            actor!.runTiming(
                Timing.actionBegin,
                { ...context, timing: Timing.actionBegin, skill },
                skill?.getEffectsAt(Timing.actionBegin),
            );
            if (operation.kind === "skill") {
                if (!skill) {
                    executed = false;
                    blockReason = "技能不属于当前出战精灵";
                } else {
                    result = move.useSkill(
                        actor!,
                        skill,
                        context.objPet,
                        context.sbjSeer,
                        context.objSeer,
                        side,
                        this.events,
                    );
                    executed = result.used;
                    blockReason = result.blockReason;
                }
            } else if (operation.kind === "prop") {
                /**
                 * prop，供本模块相关规则使用。
                 */
                const prop = context.sbjSeer.propPackage.find(
                    (item) => item.type === operation.propType,
                );
                executed = prop?.use(actor!) ?? false;
                if (!executed) blockReason = "道具不可用";
            }
        } else blockReason = "当前精灵已阵亡";
        this.events.push({ timing: Timing.actionEnd, side });
        if (context)
            actor!.runTiming(
                Timing.actionEnd,
                { ...context, timing: Timing.actionEnd, skill },
                skill?.getEffectsAt(Timing.actionEnd),
            );
        /**
         * defeatedNames，供本模块相关规则使用。
         */
        actor?.tickDurations();
        const defeatedNames = this.detectDefeat(work);
        // 切换动画已经在回合开始前播放，这里只记录新精灵的出手结束与计时。
        /**
         * actionType，供本模块相关规则使用。
         */
        const actionType =
            operation.kind === "switch"
                ? "settle"
                : operation.kind === "surrender"
                  ? "skip"
                  : operation.kind;
        /**
         * recordedSkill，供本模块相关规则使用。
         */
        const recordedSkill =
            skill ??
            (operation.kind === "skill"
                ? this.seer(work, side).petPackage.find((pet) => pet.序号 === operation.petId)
                      ?.技能组[operation.skillIndex]
                : undefined);
        this.step(
            work,
            {
                side,
                operation,
                actionType,
                timing: operation.kind === "switch" ? Timing.actionEnd : undefined,
                actorName: actor?.名字 ?? this.seer(work, side).getBattlePet()?.名字,
                skillName: recordedSkill?.name,
                skill: recordedSkill ? cloneBattleState(recordedSkill) : undefined,
                result,
                propName:
                    operation.kind === "prop"
                        ? this.seer(work, side).propPackage.find(
                              (prop) => prop.type === operation.propType,
                          )?.name
                        : undefined,
                criticalHit: result?.criticalHit,
                defeated: defeatedNames.length > 0,
                defeatedNames,
                notExecuted: !executed,
                blockReason,
            },
            from,
        );
    }

    /**
     * order：执行对应的领域操作。
     * @param work 本回合正在结算的工作局面
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private order(work: Situation): Side[] {
        /**
         * left，供本模块相关规则使用。
         */
        const left = this.leftMove.operation;
        /**
         * right，供本模块相关规则使用。
         */
        const right = this.rightMove.operation;
        /**
         * bothSkills，供本模块相关规则使用。
         */
        const bothSkills = left.kind === "skill" && right.kind === "skill";
        this.effectivePriority = { left: 0, right: 0 };
        if (bothSkills) {
            for (const side of ["left", "right"] as Side[]) {
                /**
                 * pet，供本模块相关规则使用。
                 */
                const pet = this.seer(work, side).getActivePet();
                /**
                 * operation，供本模块相关规则使用。
                 */
                const operation = this.getMove(side).operation;
                /**
                 * skill，供本模块相关规则使用。
                 */
                const skill =
                    operation.kind === "skill" ? pet?.技能组[operation.skillIndex] : undefined;
                this.effectivePriority[side] =
                    pet && skill ? pet.getSkillPreemptiveLevel(skill, this.context(work, side)) : 0;
            }
            if (this.effectivePriority.left !== this.effectivePriority.right)
                return this.effectivePriority.left > this.effectivePriority.right
                    ? ["left", "right"]
                    : ["right", "left"];
        } else if (left.kind === "skill" || right.kind === "skill")
            return left.kind === "skill" ? ["left", "right"] : ["right", "left"];
        /**
         * speed，供本模块相关规则使用。
         */
        const speed = (side: Side) => {
            /**
             * pet，供本模块相关规则使用。
             */
            const pet = this.seer(work, side).getActivePet();
            return (
                pet?.getBattleAbilityValue(
                    pet.能力值.速度,
                    pet.effectiveLevel("speed", this.seer(work, opposite(side)).getActivePet()),
                ) ?? 0
            );
        };
        /**
         * delta，供本模块相关规则使用。
         */
        const delta = speed("left") - speed("right");
        return delta > 0 || (delta === 0 && work.random.next() < 0.5)
            ? ["left", "right"]
            : ["right", "left"];
    }

    /**
     * detectDefeat：执行对应的领域操作。
     * @param work 本回合正在结算的工作局面
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private detectDefeat(work: Situation): string[] {
        /**
         * names，供本模块相关规则使用。
         */
        const names: string[] = [];
        /**
         * handled，供本模块相关规则使用。
         */
        const handled = new Set<Pet>();
        // 同一判定点中，回调导致的新阵亡继续结算；每个实例只触发一次。
        while (true) {
            /**
             * deaths，供本模块相关规则使用。
             */
            const deaths = (["left", "right"] as Side[]).flatMap((side) => {
                /**
                 * pet，供本模块相关规则使用。
                 */
                const pet = this.seer(work, side).getActivePet();
                return pet && pet.当前体力 <= 0 && !handled.has(pet) ? [{ side, pet }] : [];
            });
            if (!deaths.length) break;
            for (const { side, pet } of deaths) {
                handled.add(pet);
                this.seer(work, side).markPetDead(pet);
                names.push(pet.名字);
            }
            for (const { side, pet } of deaths) {
                /**
                 * seer，供本模块相关规则使用。
                 */
                const seer = this.seer(work, side);
                /**
                 * opponentSeer，供本模块相关规则使用。
                 */
                const opponentSeer = this.seer(work, opposite(side));
                /**
                 * opponent，供本模块相关规则使用。
                 */
                const opponent = opponentSeer.getBattlePet();
                this.events.push({ timing: Timing.petDead, side });
                if (opponent) {
                    pet.runGlobalTiming(Timing.petDead, {
                        sbjPet: pet,
                        objPet: opponent,
                        sbjSeer: seer,
                        objSeer: opponentSeer,
                        timing: Timing.petDead,
                        side,
                        events: this.events,
                    });
                    if (opponent.战斗状态 !== BattleStatus.DEAD && opponent.isAlive()) {
                        this.events.push({ timing: Timing.onDefeat, side: opposite(side) });
                        opponent.runGlobalTiming(Timing.onDefeat, {
                            sbjPet: opponent,
                            objPet: pet,
                            sbjSeer: opponentSeer,
                            objSeer: seer,
                            timing: Timing.onDefeat,
                            side: opposite(side),
                            events: this.events,
                        });
                        opponent.onDefeat(pet, opponentSeer, seer);
                    }
                    pet.onDefeated(opponent, seer, opponentSeer);
                }
                pet.leaveBattle(BattleStatus.DEAD);
            }
        }
        return names;
    }

    /**
     * describeOperation：执行对应的领域操作。
     * @param side 操作所属的一方
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    describeOperation(side: Side): string {
        /**
         * operation，供本模块相关规则使用。
         */
        const operation = this.getMove(side).operation;
        /**
         * seer，供本模块相关规则使用。
         */
        const seer = this.initialSnapshot && this.seer(this.initialSnapshot, side);
        if (operation.kind === "skill")
            return (
                seer?.petPackage.find((pet) => pet.序号 === operation.petId)?.技能组[
                    operation.skillIndex
                ]?.name ?? "技能"
            );
        if (operation.kind === "switch")
            return `切换·${seer?.petPackage.find((pet) => pet.序号 === operation.petId)?.名字 ?? operation.petId}`;
        if (operation.kind === "prop")
            return `道具·${seer?.propPackage.find((prop) => prop.type === operation.propType)?.name ?? operation.propType}`;
        return operation.kind === "surrender" ? "认输" : "空过";
    }
}
